import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../services/AuthContext';
import { X, Download, Plus, Link2, Copy, Clock, Check, Circle, Trash2, Mail, AlertTriangle, Upload, Calendar } from 'lucide-react';
import ImportarAltas from './ImportarAltas';
import { aLocal, ahoraLocal, esPasada, proximaOrientacion, textoLocal } from '../../services/orientacion';

const STATUS_MAP = {
  pending: 'Pendiente', in_progress: 'En progreso', completed: 'Completado',
  approved: 'Aprobada', rejected: 'Rechazada',
};
const STATUS_BADGE = {
  pending: 'warning', in_progress: 'info', completed: 'success',
  approved: 'success', rejected: 'danger',
};
// Estado del correo de bienvenida. Solo se muestra con el envío de correo
// activado: hasta entonces ningún correo sale y no hay estados que enseñar.
const ENVIO_MAP = {
  pendiente: ['Pendiente', 'warning'], enviado: ['Enviado', 'info'],
  entregado: ['Entregado', 'success'], rebotado: ['Rebotado', 'danger'],
  fallido: ['Fallido', 'danger'],
};
const conProblemaEnvio = (s) => ['rebotado', 'fallido'].includes(s.envio_bienvenida?.estado);
const CORREO_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const fmtFechaHora = (d) => d ? new Date(d).toLocaleString('es-ES', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
}) : '';

function EnvioBadge({ envio }) {
  if (!envio) return <span style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>—</span>;
  const [texto, tipo] = ENVIO_MAP[envio.estado] || [envio.estado, 'neutral'];
  return (
    <span className={`badge badge--${tipo}`} title={envio.ultimo_error || envio.destinatario}>
      {texto}
    </span>
  );
}

// PRL y primer día se fusionaron en un único paso. En el servidor el alta
// conserva sus ocho fases internas, y aquí la 7 y la 8 se muestran juntas como
// «Día 1»: así las altas que ya estaban en curso no se ven afectadas.
const PHASE_NAMES = ['Datos', 'Unif.', 'Accesos', 'Contrato', 'Docs', 'Examen', 'Día 1'];

const PhaseTimeline = ({ submission }) => {
  const s = submission;
  const terminada = s.status === 'completed' || s.status === 'approved';
  const current = Math.min(s.current_phase || 1, 7);
  const timestamps = [
    s.phase1_completed_at, s.phase2_completed_at, s.phase3_completed_at,
    s.phase4_completed_at, s.phase5_completed_at, s.phase6_completed_at,
    terminada ? (s.phase8_completed_at || s.phase7_completed_at) : null,
  ];
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : '';

  return (
    <div className="phase-timeline">
      {PHASE_NAMES.map((name, i) => {
        const phase = i + 1;
        const isDone = terminada || current > phase;
        const isCurrent = current === phase && !terminada;
        return (
          <React.Fragment key={i}>
            {i > 0 && <div className={`phase-timeline__line ${isDone ? 'phase-timeline__line--done' : ''}`} />}
            <div className={`phase-timeline__step ${isDone ? 'phase-timeline__step--done' : ''} ${isCurrent ? 'phase-timeline__step--current' : ''}`}
                 title={`${name}${timestamps[i] ? ` — ${fmtDate(timestamps[i])}` : ''}`}>
              <div className="phase-timeline__icon">
                {isDone ? <Check size={10} /> : isCurrent ? <Clock size={10} /> : <Circle size={8} />}
              </div>
              <span className="phase-timeline__label">{name}</span>
              {timestamps[i] && <span className="phase-timeline__date">{fmtDate(timestamps[i])}</span>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Solo la versión más reciente de cada documento. Antes de que subir de nuevo
// sustituyera al anterior, cada intento quedaba guardado y había documentos
// repetidos hasta tres veces; aquí se muestra solo el vigente.
function vigentes(files, origen) {
  const porClave = {};
  (files || [])
    .filter(f => f.source === origen)
    .sort((a, b) => new Date(a.uploaded_at) - new Date(b.uploaded_at))
    .forEach(f => { porClave[f.document_type || f.label] = f; });
  return Object.values(porClave);
}

function DocBlock({ titulo, files, color, destacado, plegado }) {
  const [abierto, setAbierto] = React.useState(!plegado);
  if (!files.length) return null;
  const azul = color === 'blue';
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 'var(--radius-sm)',
      background: azul ? 'var(--blue-50)' : destacado ? 'white' : 'var(--gray-50)',
      border: `1px solid ${azul ? 'var(--blue-200)' : 'var(--gray-200)'}`,
    }}>
      <div onClick={(e) => { e.stopPropagation(); if (plegado) setAbierto(!abierto); }}
        style={{ display: 'flex', alignItems: 'center', cursor: plegado ? 'pointer' : 'default' }}>
        <strong style={{ fontSize: '0.84rem', color: azul ? 'var(--blue-800)' : 'var(--gray-700)' }}>
          {titulo} ({files.length})
        </strong>
        {plegado && (
          <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--gray-500)' }}>
            {abierto ? 'Ocultar' : 'Mostrar'}
          </span>
        )}
      </div>
      {abierto && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {files.map((f, i) => (
            <a key={i} href={f.file} target="_blank" rel="noreferrer" download
               onClick={(e) => e.stopPropagation()}
               style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--blue-600)' }}>
              <Download size={12} /> {f.label}
              <span style={{ marginLeft: 'auto', color: 'var(--gray-400)', fontSize: '0.75rem' }}>
                {new Date(f.uploaded_at).toLocaleDateString('es-ES')}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminSubmissions() {
  const { isSuperUser, envioCorreoActivo } = useAuth();
  const [descargando, setDescargando] = useState(null);

  // Descarga el .zip con la documentación vigente del empleado.
  const descargarZip = async (sub) => {
    setDescargando(sub.id);
    try {
      const res = await adminApi.downloadSubmissionZip(sub.id);
      const cab = res.headers['content-disposition'] || '';
      const m = cab.match(/filename="?([^"]+)"?/);
      const nombre = m ? m[1] : `${sub.first_name}_${sub.last_name}.zip`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = nombre;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo preparar la descarga.');
    }
    setDescargando(null);
  };
  const [submissions, setSubmissions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  // Altas finalizadas: con los dos exámenes de manipulador aprobados y pasadas
  // las 22:30 (hora peninsular) del día del examen. Lo calcula el servidor
  // (alta_finalizada), para no depender del reloj de quien consulta.
  const [pestana, setPestana] = useState('proceso');
  const [busqueda, setBusqueda] = useState('');
  const [restauranteFiltro, setRestauranteFiltro] = useState('');
  const [approveModal, setApproveModal] = useState(null);
  const [createModal, setCreateModal] = useState(null);
  const [creating, setCreating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  // Correo de bienvenida: confirmación antes de crear, filtro de problemas,
  // ventana de reenvío e historial de cada alta (se carga al pedirlo).
  const [confirmando, setConfirmando] = useState(false);
  const [soloProblemas, setSoloProblemas] = useState(false);
  const [reenvioModal, setReenvioModal] = useState(null);
  const [reenviando, setReenviando] = useState(false);
  const [historiales, setHistoriales] = useState({});
  const [plantillaProvisional, setPlantillaProvisional] = useState(false);
  // Si la plantilla cita la orientación, el reenvío necesita una fecha vigente.
  const [plantillaUsaOrientacion, setPlantillaUsaOrientacion] = useState(false);
  const [importando, setImportando] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, catRes, restRes] = await Promise.all([
        adminApi.getSubmissions({}), adminApi.getCategories(), adminApi.getRestaurants(),
      ]);
      setSubmissions(subRes.data.results || subRes.data);
      setCategories(catRes.data.results || catRes.data);
      setRestaurants(restRes.data.results || restRes.data);
    } catch { toast.error('Error al cargar.'); }
    setLoading(false);
  }, []);

  const normalizar = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const finalizada = (x) => !!x.alta_finalizada;
  const enPestana = submissions.filter(x => (pestana === 'finalizadas') === finalizada(x));
  const nProblemas = envioCorreoActivo ? enPestana.filter(conProblemaEnvio).length : 0;
  const visibles = enPestana.filter(x => {
    if (soloProblemas && !conProblemaEnvio(x)) return false;
    if (restauranteFiltro && String(x.restaurant) !== restauranteFiltro) return false;
    if (!busqueda.trim()) return true;
    const texto = normalizar(`${x.first_name} ${x.last_name} ${x.dni || ''}`);
    return normalizar(busqueda).split(/\s+/).every(t => texto.includes(t));
  });
  const nProceso = submissions.filter(x => !finalizada(x)).length;
  const nFinalizadas = submissions.length - nProceso;

  useEffect(() => { load(); }, [load]);

  // Con la plantilla provisional, el correo no sale a los candidatos: se avisa.
  useEffect(() => {
    if (!envioCorreoActivo) return;
    adminApi.getPlantilla('bienvenida')
      .then(res => {
        setPlantillaProvisional(res.data.es_provisional);
        setPlantillaUsaOrientacion(!!res.data.usa_orientacion);
      })
      .catch(() => {});
  }, [envioCorreoActivo]);

  const handleApprove = async () => {
    if (!approveModal?.employee_number) { toast.error('Introduce el número de empleado.'); return; }
    try {
      await adminApi.approveSubmission(approveModal.id, {
        employee_number: approveModal.employee_number,
        category: approveModal.category || null,
        start_date: approveModal.start_date || null,
      });
      toast.success('Solicitud aprobada y empleado creado.');
      setApproveModal(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error al aprobar.'); }
  };

  const enviaBienvenida = envioCorreoActivo && createModal?.enviar;

  // Comprueba el formulario. Si se va a enviar el correo, antes se pide
  // confirmación con el nombre y la dirección, para cazar erratas.
  const handleCreateOnboarding = () => {
    if (!createModal.first_name || !createModal.last_name || !createModal.restaurant) {
      toast.error('Nombre, apellidos y restaurante son obligatorios.'); return;
    }
    if (envioCorreoActivo) {
      const email = (createModal.email || '').trim();
      if (createModal.enviar && !email) {
        toast.error('Indica el correo del candidato o desmarca el envío.'); return;
      }
      if (email && !CORREO_RE.test(email)) {
        toast.error('La dirección de correo no es válida.'); return;
      }
      if (createModal.enviar && !createModal.fecha_orientacion) {
        toast.error('Indica la fecha y hora de la orientación.'); return;
      }
      if (esPasada(createModal.fecha_orientacion)) {
        toast.error('La fecha de la orientación ya ha pasado.'); return;
      }
    }
    if (enviaBienvenida) { setConfirmando(true); return; }
    crearAlta();
  };

  const crearAlta = async () => {
    setCreating(true);
    const datos = {
      first_name: createModal.first_name, last_name: createModal.last_name,
      restaurant: createModal.restaurant,
    };
    if (envioCorreoActivo) {
      datos.email = (createModal.email || '').trim();
      datos.enviar_bienvenida = !!createModal.enviar;
      datos.fecha_orientacion = createModal.fecha_orientacion || '';
    }
    try {
      const res = await adminApi.createOnboarding(datos);
      setGeneratedLink({
        url: `${window.location.origin}/onboarding/${res.data.token}`,
        code: res.data.access_code,
        name: `${res.data.first_name} ${res.data.last_name}`,
        expires: new Date(res.data.expires_at).toLocaleDateString('es-ES'),
        email: res.data.email,
        envio: res.data.envio,
      });
      toast.success('Invitación creada.'); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error al crear.'); }
    setConfirmando(false);
    setCreating(false);
  };

  const cerrarCreacion = () => { setGeneratedLink(null); setCreateModal(null); setConfirmando(false); };

  const verHistorial = async (sub) => {
    try {
      const res = await adminApi.getEnvios({ submission: sub.id });
      setHistoriales(h => ({ ...h, [sub.id]: res.data.results || res.data }));
    } catch { toast.error('No se pudo cargar el historial.'); }
  };

  const handleReenviar = async () => {
    const email = (reenvioModal.email || '').trim();
    if (!CORREO_RE.test(email)) { toast.error('La dirección de correo no es válida.'); return; }
    const { fecha, fechaOriginal } = reenvioModal;
    if (pideFechaReenvio(reenvioModal) && !fecha) {
      toast.error('Indica la fecha y hora de la orientación.'); return;
    }
    if (fecha && fecha !== fechaOriginal && esPasada(fecha)) {
      toast.error('La fecha de la orientación ya ha pasado.'); return;
    }
    setReenviando(true);
    try {
      const datos = { email, regenerar_contrasena: reenvioModal.regenerar };
      // Solo se envía si cambia: así un alta sin la orientación en la
      // plantilla no tiene por qué llevarla.
      if (fecha && fecha !== fechaOriginal) datos.fecha_orientacion = fecha;
      const res = await adminApi.reenviarBienvenida(reenvioModal.sub.id, datos);
      const estado = res.data.envio?.estado;
      if (estado === 'enviado') toast.success(`Correo enviado a ${email}.`);
      else if (estado === 'fallido') toast.error('El correo no se ha podido enviar. Revisa la dirección.');
      else toast('No se ha podido enviar todavía; se reintentará automáticamente.', { icon: '⏳' });
      setHistoriales(h => { const n = { ...h }; delete n[reenvioModal.sub.id]; return n; });
      setReenvioModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error al reenviar.'); }
    setReenviando(false);
  };

  // El correo de PRL sale solo al aprobar los exámenes; esto lo repite.
  const handleReenviarPrl = async (sub) => {
    if (!window.confirm(`¿Reenviar a ${sub.email} el correo con las instrucciones del curso de PRL?`)) return;
    try {
      const res = await adminApi.reenviarPrl(sub.id);
      const estado = res.data.envio?.estado;
      if (estado === 'enviado') toast.success(`Correo de PRL enviado a ${sub.email}.`);
      else if (estado === 'fallido') toast.error('El correo no se ha podido enviar. Revisa la dirección.');
      else toast('No se ha podido enviar todavía; se reintentará automáticamente.', { icon: '⏳' });
      setHistoriales(h => { const n = { ...h }; delete n[sub.id]; return n; });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error al reenviar.'); }
  };

  // La fecha es obligatoria en el reenvío si la plantilla cita la orientación
  // y el alta no tiene una vigente (altas antiguas o con la fecha ya pasada).
  const pideFechaReenvio = (m) => plantillaUsaOrientacion && (!m.fechaOriginal || esPasada(m.fechaOriginal));

  const abrirReenvio = (s) => {
    const fechaOriginal = aLocal(s.fecha_orientacion);
    setReenvioModal({
      sub: s, email: s.email || '', regenerar: false, fechaOriginal,
      fecha: fechaOriginal && !esPasada(fechaOriginal) ? fechaOriginal : '',
    });
  };

  // Borrado definitivo de una solicitud, con sus archivos. Solo superusuarios.
  const handlePurge = async (sub) => {
    if (!window.confirm(
      `Se eliminará definitivamente la solicitud de ${sub.first_name} `
      + `${sub.last_name} y todos sus archivos subidos.\n\n`
      + 'Esta acción no se puede deshacer. ¿Continuar?'
    )) return;
    try {
      const res = await adminApi.purgeSubmission(sub.id);
      toast.success(res.data?.mensaje || 'Solicitud eliminada.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al eliminar.');
    }
  };

  const copyLink = () => { navigator.clipboard.writeText(generatedLink.url); toast.success('Enlace copiado.'); };
  const copyCode = () => { navigator.clipboard.writeText(generatedLink.code || ''); toast.success('Contraseña copiada.'); };
  const copyBoth = () => {
    navigator.clipboard.writeText(
      `Enlace de alta: ${generatedLink.url}\nContraseña de acceso: ${generatedLink.code || ''}`
    );
    toast.success('Enlace y contraseña copiados.');
  };
  // La API devuelve URLs absolutas; no se antepone ningún prefijo.

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div><h1>Nuevos Empleados</h1><p>Crea invitaciones de onboarding y gestiona las altas</p></div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {envioCorreoActivo && (
            <Link to="/gestion/solicitudes/correo" className="btn btn--secondary">
              <Mail size={16} /> Correos automáticos
            </Link>
          )}
          {envioCorreoActivo && (
            <button className="btn btn--secondary" onClick={() => setImportando(true)}>
              <Upload size={16} /> Importar altas
            </button>
          )}
          <button className="btn btn--primary" onClick={() => setCreateModal({ first_name: '', last_name: '', restaurant: '', email: '', enviar: !plantillaProvisional, fecha_orientacion: proximaOrientacion() })}>
            <Plus size={16} /> Crear alta
          </button>
        </div>
      </div>

      {envioCorreoActivo && plantillaProvisional && (
        <div className="alert alert--warning" style={{ fontSize: '0.86rem', marginBottom: 16 }}>
          <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> La plantilla del correo de
          bienvenida es provisional y no se envía a los candidatos.{' '}
          <Link to="/gestion/solicitudes/correo">Revisarla</Link>. Mientras tanto, las altas pueden
          crearse sin enviar el correo y copiar el enlace y la contraseña.
        </div>
      )}

      <div className="tabs" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className={`btn btn--sm ${pestana === 'proceso' ? 'btn--primary' : 'btn--secondary'}`}
          onClick={() => setPestana('proceso')}>
          Altas en proceso ({nProceso})
        </button>
        <button className={`btn btn--sm ${pestana === 'finalizadas' ? 'btn--primary' : 'btn--secondary'}`}
          onClick={() => setPestana('finalizadas')}>
          Altas finalizadas ({nFinalizadas})
        </button>
      </div>
      <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)', margin: '-6px 0 14px' }}>
        Las altas pasan a finalizadas a las 22:30 del día en que el empleado aprueba el examen
        de manipulador de alimentos.
      </p>

      <div className="filter-bar" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input className="form-input" style={{ flex: '1 1 260px' }} value={busqueda}
          onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por nombre o DNI" />
        <select className="form-input" style={{ maxWidth: 260 }} value={restauranteFiltro}
          onChange={e => setRestauranteFiltro(e.target.value)}>
          <option value="">Todos los restaurantes</option>
          {restaurants.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
        </select>
        {(nProblemas > 0 || soloProblemas) && (
          <button className={`btn btn--sm ${soloProblemas ? 'btn--primary' : 'btn--secondary'}`}
            style={soloProblemas ? undefined : { color: 'var(--danger)', borderColor: 'var(--danger)' }}
            onClick={() => setSoloProblemas(!soloProblemas)}
            title="Correos rebotados o que no se han podido enviar">
            <AlertTriangle size={14} /> {soloProblemas ? 'Mostrar todas' : `${nProblemas} con problemas de envío`}
          </button>
        )}
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="card">
          {visibles.length === 0 ? (
            <div className="empty-state"><p>{enPestana.length === 0
              ? (pestana === 'proceso' ? 'No hay altas en proceso' : 'No hay altas finalizadas')
              : 'Ninguna alta coincide con la búsqueda'}</p></div>
          ) : (
            <table className="data-table">
              <thead><tr>
                <th>Fecha</th><th>Nombre</th><th>Restaurante</th><th>Progreso</th><th>Estado</th>
                {envioCorreoActivo && <th>Correo</th>}<th>Acciones</th>
              </tr></thead>
              <tbody>
                {visibles.map(s => (
                  <React.Fragment key={s.id}>
                    <tr onClick={() => setExpandedRow(expandedRow === s.id ? null : s.id)}
                      style={{ cursor: 'pointer', ...(envioCorreoActivo && conProblemaEnvio(s) ? { background: 'var(--danger-light)' } : {}) }}>
                      <td>{new Date(s.created_at).toLocaleDateString('es-ES')}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{s.first_name} {s.last_name}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>{s.dni || `Contraseña: ${s.access_code || '—'}`}</div>
                      </td>
                      <td>{s.restaurant_name}</td>
                      <td><PhaseTimeline submission={s} /></td>
                      <td><span className={`badge badge--${STATUS_BADGE[s.status] || 'warning'}`}>{STATUS_MAP[s.status] || s.status}</span></td>
                      {envioCorreoActivo && <td><EnvioBadge envio={s.envio_bienvenida} /></td>}
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {s.onboarding_url && s.is_link_active && (
                            <button className="btn btn--ghost btn--sm" title="Copiar enlace y contraseña"
                              onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(`Enlace de alta: ${window.location.origin}${s.onboarding_url}\nContraseña de acceso: ${s.access_code || ''}`); toast.success('Enlace y contraseña copiados.'); }}>
                              <Link2 size={14} />
                            </button>
                          )}
                          {isSuperUser && (
                            <button className="btn btn--ghost btn--sm"
                              title="Eliminar definitivamente esta solicitud y sus archivos"
                              onClick={(e) => { e.stopPropagation(); handlePurge(s); }}>
                              <Trash2 size={14} color="var(--danger)" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded detail row */}
                    {expandedRow === s.id && (
                      <tr className="expanded-row">
                        <td colSpan={envioCorreoActivo ? 7 : 6}>
                          <div className="expanded-detail">
                            <div className="expanded-detail__grid">
                              <div><strong>DNI:</strong> {s.dni || '— (lo aporta el empleado)'}</div>
                              <div>
                                <strong>Contraseña de acceso:</strong>{' '}
                                <code style={{ letterSpacing: '0.1em', fontWeight: 600 }}>{s.access_code || '—'}</code>
                                {s.access_code && (
                                  <button className="btn btn--ghost btn--sm" title="Copiar contraseña"
                                    onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(s.access_code); toast.success('Contraseña copiada.'); }}>
                                    <Copy size={12} />
                                  </button>
                                )}
                              </div>
                              <div><strong>SS:</strong> {s.social_security_number || '—'}</div>
                              <div><strong>IBAN:</strong> {s.iban || '—'}</div>
                              <div>
                                <strong>Accesos:</strong>{' '}
                                {s.access_uniformes && '✓Uniformes '}
                                {s.access_cam && '✓Campus '}
                                {s.access_orquest && '✓Orquest'}
                                {!s.access_uniformes && !s.access_cam && !s.access_orquest && '—'}
                              </div>
                              <div>
                                <strong>Cursos previos de Campus:</strong>{' '}
                                {s.courses_done
                                  ? <span style={{ color: 'var(--success)' }}>✓ completados</span>
                                  : <span style={{ color: 'var(--amber-600, #b45309)' }}>pendientes</span>}
                              </div>
                              <div>
                                <strong>Vacaciones indicadas:</strong>{' '}
                                {s.vacation_form_done ? '✓ sí' : 'no'}
                              </div>
                              <div>
                                <strong>Documentación contractual:</strong>{' '}
                                {(s.files || []).some(f => f.source === 'gestoria')
                                  ? ((s.files || []).some(f => f.source !== 'gestoria' && /contrato|anexo|145/i.test(f.label || ''))
                                      ? 'entregada y firmada (parcial o total)'
                                      : 'entregada, sin firmar')
                                  : 'gestoría aún no la ha subido'}
                              </div>
                              <div>
                                <strong>Examen 1:</strong>{' '}
                                {s.exam1_passed ? `✓ ${s.exam1_score}/63` : s.exam1_score != null ? `✗ ${s.exam1_score}/63` : '—'}
                              </div>
                              <div>
                                <strong>Examen 2:</strong>{' '}
                                {s.exam2_passed ? `✓ ${s.exam2_score}/20` : s.exam2_score != null ? `✗ ${s.exam2_score}/20` : '—'}
                              </div>
                            </div>
                            {envioCorreoActivo && (
                              <div style={{
                                marginTop: 12, padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                                background: conProblemaEnvio(s) ? 'var(--danger-light)' : 'var(--gray-50)',
                                border: '1px solid var(--gray-200)', fontSize: '0.84rem',
                              }} onClick={(e) => e.stopPropagation()}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                  <Calendar size={14} />
                                  <strong>Orientación:</strong> {s.orientacion_texto || '— (sin fecha)'}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <Mail size={14} />
                                  <strong>Correo:</strong> {s.email || '— (sin correo)'}
                                  <EnvioBadge envio={s.envio_bienvenida} />
                                  {s.envio_bienvenida?.estado === 'pendiente' && s.envio_bienvenida.proximo_intento && (
                                    <span style={{ color: 'var(--gray-500)' }}>
                                      próximo intento: {fmtFechaHora(s.envio_bienvenida.proximo_intento)}
                                    </span>
                                  )}
                                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                                    <button className="btn btn--secondary btn--sm" onClick={() => verHistorial(s)}>
                                      Ver historial
                                    </button>
                                    {s.is_link_active && new Date(s.token_expires_at) > new Date() && (
                                      <button className="btn btn--primary btn--sm"
                                        onClick={() => abrirReenvio(s)}>
                                        <Mail size={12} /> {s.envio_bienvenida ? 'Reenviar bienvenida' : 'Enviar bienvenida'}
                                      </button>
                                    )}
                                  </span>
                                </div>
                                {conProblemaEnvio(s) && s.envio_bienvenida.ultimo_error && (
                                  <div style={{ marginTop: 6, color: 'var(--danger)', fontSize: '0.78rem' }}>
                                    {s.envio_bienvenida.ultimo_error}
                                  </div>
                                )}
                                {/* Correo del curso de PRL: sale al aprobar los exámenes */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                                  <Mail size={14} />
                                  <strong>Curso de PRL:</strong>
                                  {s.envio_prl ? <EnvioBadge envio={s.envio_prl} /> : (
                                    <span style={{ color: 'var(--gray-500)' }}>
                                      {s.exam1_passed && s.exam2_passed ? 'sin enviar' : 'sale al aprobar los exámenes'}
                                    </span>
                                  )}
                                  {s.email && s.exam1_passed && s.exam2_passed && (
                                    <button className="btn btn--secondary btn--sm" style={{ marginLeft: 'auto' }}
                                      onClick={() => handleReenviarPrl(s)}>
                                      <Mail size={12} /> {s.envio_prl ? 'Reenviar correo de PRL' : 'Enviar correo de PRL'}
                                    </button>
                                  )}
                                </div>
                                {['rebotado', 'fallido'].includes(s.envio_prl?.estado) && s.envio_prl.ultimo_error && (
                                  <div style={{ marginTop: 6, color: 'var(--danger)', fontSize: '0.78rem' }}>
                                    {s.envio_prl.ultimo_error}
                                  </div>
                                )}
                                {historiales[s.id] && (
                                  historiales[s.id].length === 0
                                    ? <div style={{ marginTop: 8, color: 'var(--gray-500)' }}>No hay envíos registrados.</div>
                                    : (
                                      <table className="data-table" style={{ marginTop: 8, fontSize: '0.8rem' }}>
                                        <thead><tr><th>Fecha</th><th>Tipo</th><th>Destinatario</th><th>Estado</th><th>Intentos</th></tr></thead>
                                        <tbody>
                                          {historiales[s.id].map(e => (
                                            <tr key={e.id} title={e.ultimo_error || ''}>
                                              <td>{fmtFechaHora(e.creado)}</td>
                                              <td>{e.tipo_display}</td>
                                              <td>{e.destinatario}</td>
                                              <td><EnvioBadge envio={e} /></td>
                                              <td>{e.intentos}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )
                                )}
                              </div>
                            )}

                            {/* Descarga de toda la documentación en un .zip */}
                            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
                              <button className="btn btn--primary btn--sm"
                                onClick={(e) => { e.stopPropagation(); descargarZip(s); }}
                                disabled={descargando === s.id}>
                                <Download size={14} /> {descargando === s.id ? 'Preparando...' : 'Descargar todo (.zip)'}
                              </button>
                            </div>

                            {/* Documentos, separados por quién los aporta */}
                            <DocBlock titulo="Documentación del empleado"
                              files={vigentes(s.files, 'employee')} destacado />
                            <DocBlock titulo="Manipulador de alimentos"
                              files={vigentes(s.files, 'sistema')} color="blue" />
                            <DocBlock titulo="Entregado por gestoría (sin firmar)"
                              files={vigentes(s.files, 'gestoria')} plegado />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {envioCorreoActivo && importando && (
        <ImportarAltas plantillaProvisional={plantillaProvisional}
          onClose={() => setImportando(false)} onCreadas={load} />
      )}

      {/* Create Onboarding Modal */}
      {createModal && !generatedLink && !confirmando && (
        <div className="modal-overlay" onClick={() => setCreateModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Crear alta de nuevo empleado</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setCreateModal(null)}><X size={18} /></button>
            </div>
            <p style={{ color: 'var(--gray-500)', fontSize: '0.88rem', marginBottom: 16 }}>
              Introduce los datos del nuevo empleado. Se generará un enlace y una contraseña temporal.
              El DNI, el número de la Seguridad Social y el IBAN los aportará el propio empleado en el
              primer paso de su alta.
            </p>
            <div className="form-row">
              <div className="form-group"><label>Nombre *</label><input className="form-input" value={createModal.first_name} onChange={e => setCreateModal({ ...createModal, first_name: e.target.value })} /></div>
              <div className="form-group"><label>Apellidos *</label><input className="form-input" value={createModal.last_name} onChange={e => setCreateModal({ ...createModal, last_name: e.target.value })} /></div>
            </div>
            <div className="form-group">
              <label>Restaurante *</label>
              <select className="form-input" value={createModal.restaurant} onChange={e => setCreateModal({ ...createModal, restaurant: e.target.value })}>
                <option value="">Selecciona</option>
                {restaurants.map(r => <option key={r.id} value={r.id}>{r.name} ({r.code})</option>)}
              </select>
            </div>
            {envioCorreoActivo && (
              <>
                <div className="form-group">
                  <label>Correo electrónico{createModal.enviar ? ' *' : ''}</label>
                  <input className="form-input" type="email" value={createModal.email}
                    placeholder="nombre@ejemplo.com"
                    onChange={e => setCreateModal({ ...createModal, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>
                    <input type="checkbox" checked={createModal.enviar}
                      onChange={e => setCreateModal({ ...createModal, enviar: e.target.checked })} style={{ marginRight: 8 }} />
                    Enviar correo de bienvenida con el enlace y la contraseña
                  </label>
                </div>
                <div className="form-group">
                  <label>Fecha y hora de la orientación{createModal.enviar ? ' *' : ''}</label>
                  <input className="form-input" type="datetime-local" value={createModal.fecha_orientacion}
                    min={ahoraLocal()}
                    onChange={e => setCreateModal({ ...createModal, fecha_orientacion: e.target.value })} />
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 4 }}>
                    Se propone el próximo martes a las 10:00. Aparece en el correo si la plantilla la
                    cita y en la página de alta del candidato.
                  </div>
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setCreateModal(null)}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleCreateOnboarding} disabled={creating}>
                {creating ? 'Creando...' : enviaBienvenida ? 'Crear y enviar' : 'Generar enlace'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmación del envío: nombre y destinatario, para cazar erratas */}
      {createModal && confirmando && !generatedLink && (
        <div className="modal-overlay" onClick={() => setConfirmando(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Confirmar el envío</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setConfirmando(false)}><X size={18} /></button>
            </div>
            <p style={{ fontSize: '0.92rem', marginBottom: 12 }}>
              Se creará el alta y se enviará el correo de bienvenida con el enlace y la contraseña a:
            </p>
            <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ fontWeight: 600 }}>{createModal.first_name} {createModal.last_name}</div>
              <div style={{ fontSize: '1.05rem', marginTop: 4 }}>{(createModal.email || '').trim()}</div>
              <div style={{ fontSize: '0.88rem', marginTop: 8, color: 'var(--gray-600)' }}>
                Orientación: {textoLocal(createModal.fecha_orientacion)}
              </div>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--gray-500)', marginTop: 12 }}>
              Comprueba la dirección letra a letra: si está mal escrita, el correo no llegará.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setConfirmando(false)} disabled={creating}>Corregir</button>
              <button className="btn btn--primary" onClick={crearAlta} disabled={creating}>
                <Mail size={14} /> {creating ? 'Enviando...' : 'Crear y enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Link Modal */}
      {generatedLink && (
        <div className="modal-overlay" onClick={cerrarCreacion}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Enlace generado</h3>
              <button className="btn btn--ghost btn--sm" onClick={cerrarCreacion}><X size={18} /></button>
            </div>
            <div className="alert alert--success" style={{ marginBottom: 16 }}>
              Alta creada para <strong>{generatedLink.name}</strong>. El enlace expira el {generatedLink.expires}.
            </div>
            {generatedLink.envio?.estado === 'enviado' && (
              <div className="alert alert--info" style={{ marginBottom: 16 }}>
                <Mail size={14} style={{ verticalAlign: 'middle' }} /> Correo de bienvenida enviado a <strong>{generatedLink.email}</strong>.
              </div>
            )}
            {generatedLink.envio?.estado === 'pendiente' && (
              <div className="alert alert--warning" style={{ marginBottom: 16 }}>
                El correo a <strong>{generatedLink.email}</strong> no se ha podido enviar todavía; se
                reintentará automáticamente. Puedes seguir su estado en el listado.
              </div>
            )}
            {generatedLink.envio?.estado === 'fallido' && (
              <div className="alert alert--danger" style={{ marginBottom: 16 }}>
                El correo a <strong>{generatedLink.email}</strong> no se ha podido enviar. Revisa la
                dirección y reenvíalo desde el listado, o copia el enlace y la contraseña.
              </div>
            )}

            <label style={{ fontSize: '0.82rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Enlace de alta</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--gray-50)', padding: '12px 16px', borderRadius: 8, border: '1px solid var(--gray-200)' }}>
              <input className="form-input" readOnly value={generatedLink.url} style={{ flex: 1, fontSize: '0.85rem', background: 'white' }} />
              <button className="btn btn--ghost btn--sm" onClick={copyLink}><Copy size={14} /> Copiar</button>
            </div>

            <label style={{ fontSize: '0.82rem', fontWeight: 600, display: 'block', margin: '16px 0 4px' }}>Contraseña de acceso</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--gray-50)', padding: '12px 16px', borderRadius: 8, border: '1px solid var(--gray-200)' }}>
              <input className="form-input" readOnly value={generatedLink.code || ''}
                style={{ flex: 1, fontSize: '1.1rem', letterSpacing: '0.15em', textAlign: 'center', fontWeight: 600, background: 'white' }} />
              <button className="btn btn--ghost btn--sm" onClick={copyCode}><Copy size={14} /> Copiar</button>
            </div>

            <div style={{ marginTop: 16 }}>
              <button className="btn btn--primary" onClick={copyBoth} style={{ width: '100%' }}>
                <Copy size={14} /> Copiar enlace y contraseña
              </button>
            </div>

            <div className="alert alert--warning" style={{ fontSize: '0.82rem', marginTop: 12 }}>
              Anota la contraseña ahora y entrégasela al empleado junto al enlace. Sin ella no
              podrá abrir su alta. Podrás volver a consultarla desde el detalle de esta solicitud.
            </div>
          </div>
        </div>
      )}

      {/* Reenvío de la bienvenida */}
      {reenvioModal && (
        <div className="modal-overlay" onClick={() => setReenvioModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>{reenvioModal.sub.envio_bienvenida ? 'Reenviar' : 'Enviar'} bienvenida: {reenvioModal.sub.first_name} {reenvioModal.sub.last_name}</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setReenvioModal(null)}><X size={18} /></button>
            </div>
            <div className="form-group">
              <label>Correo electrónico *</label>
              <input className="form-input" type="email" value={reenvioModal.email}
                onChange={e => setReenvioModal({ ...reenvioModal, email: e.target.value })} />
              {reenvioModal.sub.email && reenvioModal.email.trim() !== reenvioModal.sub.email && (
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 4 }}>
                  Se corregirá la dirección del alta (antes: {reenvioModal.sub.email}).
                </div>
              )}
            </div>
            <div className="form-group">
              <label>
                <input type="checkbox" checked={reenvioModal.regenerar}
                  onChange={e => setReenvioModal({ ...reenvioModal, regenerar: e.target.checked })} style={{ marginRight: 8 }} />
                Generar una contraseña nueva (la actual dejará de funcionar)
              </label>
            </div>
            {plantillaUsaOrientacion && (
              <div className="form-group">
                <label>Fecha y hora de la orientación{pideFechaReenvio(reenvioModal) ? ' *' : ''}</label>
                <input className="form-input" type="datetime-local" value={reenvioModal.fecha}
                  min={ahoraLocal()}
                  onChange={e => setReenvioModal({ ...reenvioModal, fecha: e.target.value })} />
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 4 }}>
                  {!reenvioModal.fechaOriginal
                    ? 'Esta alta no tiene fecha de orientación y la plantilla la cita: indícala.'
                    : esPasada(reenvioModal.fechaOriginal)
                      ? `La orientación del alta (${textoLocal(reenvioModal.fechaOriginal)}) ya ha pasado: indica la nueva.`
                      : 'Cámbiala solo si la orientación se ha movido.'}
                  {!reenvioModal.fecha && (
                    <> {' '}<button type="button" className="btn btn--ghost btn--sm" style={{ padding: '0 4px' }}
                      onClick={() => setReenvioModal({ ...reenvioModal, fecha: proximaOrientacion() })}>
                      Proponer el próximo martes
                    </button></>
                  )}
                </div>
              </div>
            )}
            {reenvioModal.regenerar && reenvioModal.sub.status === 'in_progress' && (
              <div className="alert alert--warning" style={{ fontSize: '0.82rem' }}>
                Este candidato ya ha entrado en su alta. Con una contraseña nueva, tendrá que
                volver a entrar con ella; lo que ya ha completado se conserva.
              </div>
            )}
            {reenvioModal.sub.envio_bienvenida?.estado === 'pendiente' && (
              <div className="alert alert--info" style={{ fontSize: '0.82rem' }}>
                Hay un envío pendiente de reintento: se sustituye por este, así que el candidato
                recibirá un solo correo.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setReenvioModal(null)} disabled={reenviando}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleReenviar} disabled={reenviando}>
                <Mail size={14} /> {reenviando ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {approveModal && (
        <div className="modal-overlay" onClick={() => setApproveModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Aprobar: {approveModal.first_name} {approveModal.last_name}</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setApproveModal(null)}><X size={18} /></button>
            </div>
            <div className="alert alert--info">
              DNI: {approveModal.dni || '—'} · SS: {approveModal.social_security_number || '—'}
              {approveModal.iban && <> · IBAN: {approveModal.iban}</>}
              · Restaurante: {approveModal.restaurant_name} · Fase: {approveModal.current_phase || 1}/8
            </div>
            <div className="form-group"><label>Número de empleado *</label><input className="form-input" type="number" value={approveModal.employee_number} onChange={e => setApproveModal({ ...approveModal, employee_number: e.target.value })} placeholder="Ej: 15" /></div>
            <div className="form-group"><label>Categoría profesional</label>
              <select className="form-input" value={approveModal.category} onChange={e => setApproveModal({ ...approveModal, category: e.target.value })}>
                <option value="">Seleccionar</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Fecha de inicio</label><input className="form-input" type="date" value={approveModal.start_date} onChange={e => setApproveModal({ ...approveModal, start_date: e.target.value })} /></div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setApproveModal(null)}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleApprove}>Aprobar y crear empleado</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
